import type { ReactNode } from "react";

/**
 * One license rendered as a stacked name card. Surfaces all three names an admin
 * cares about — the Google Business Profile display name, the DCC DBA, and the
 * DCC legal name — every time, even when they are identical or empty, so the
 * reviewer sees exactly what is in the database rather than a collapsed guess.
 *
 * Layout (PR #31):
 *   line 1 — GBP displayName (bold, primary) + a small [GBP] chip. When there is
 *            no gbp_profile row the line is replaced by a faint "(No GBP profile)"
 *            pill so the absence is explicit.
 *   line 2 — DBA, muted. Always shown, even if equal to the GBP name.
 *   line 3 — Legal, smaller muted.
 *   footer — license_number · city / zip, monospace small.
 *
 * Empty individual fields render a gray "(none)" rather than being omitted.
 */
export interface LicenseRowData {
  license_number: string;
  gbp_display_name?: string | null;
  dba_name?: string | null;
  legal_name?: string | null;
  premise_city?: string | null;
  premise_zip?: string | null;
}

function none(value: string | null | undefined): ReactNode {
  const trimmed = (value ?? "").trim();
  if (trimmed) return trimmed;
  return <span className="italic text-gray-400">(none)</span>;
}

export default function LicenseRow({
  license,
  leading,
  trailing,
  className = "",
}: {
  license: LicenseRowData;
  /** Optional control rendered to the left (e.g. an include checkbox). */
  leading?: ReactNode;
  /** Optional control rendered to the right (e.g. warning glyph / buttons). */
  trailing?: ReactNode;
  className?: string;
}) {
  const gbp = (license.gbp_display_name ?? "").trim();
  const city = (license.premise_city ?? "").trim();
  const zip = (license.premise_zip ?? "").trim();
  const locality = [city, zip].filter(Boolean).join(" ");

  return (
    <div className={`flex items-start gap-2 ${className}`}>
      {leading != null && <div className="mt-0.5 shrink-0">{leading}</div>}
      <div className="min-w-0 flex-1">
        {gbp ? (
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-base font-semibold text-foreground text-gray-900">{gbp}</span>
            <span className="inline-flex items-center rounded-full bg-[#E0F2FE] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#0369A1]">
              GBP
            </span>
          </div>
        ) : (
          <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">
            No GBP profile
          </span>
        )}
        <div className="text-sm text-gray-600 text-muted-foreground">DBA: {none(license.dba_name)}</div>
        <div className="text-xs text-gray-500/80 text-muted-foreground">Legal: {none(license.legal_name)}</div>
        <div className="mt-0.5 font-mono text-[11px] text-gray-400">
          {license.license_number}
          {locality && <span> · {locality}</span>}
        </div>
      </div>
      {trailing != null && <div className="shrink-0">{trailing}</div>}
    </div>
  );
}
